import { useState } from "react";
import { motion } from "framer-motion";
import { Users, User, Mail, Phone, Clock, Wrench, MessageSquare, Send, Heart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VolunteerPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    availability: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer/signup`, form);
      toast.success("Thank you for signing up!");
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm({ ...form, [field]: value });

  if (submitted) {
    return (
      <main data-testid="volunteer-success" className="min-h-screen">
        <div className="pt-28 pb-24 md:pt-36 md:pb-32 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md px-6"
          >
            <Heart className="w-16 h-16 text-[#C0603A] mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2C2A] mb-4">
              Welcome to the Team!
            </h1>
            <p className="font-body text-base text-[#6C6B66] mb-6">
              Thank you for volunteering with 333Oreo Foundation. We'll be in touch
              to get you started on making a difference for fur families everywhere.
            </p>
            <p className="font-body text-sm text-[#8FA396] italic">
              "Together we can change lives &mdash; one paw at a time."
            </p>
          </motion.div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="volunteer-page" className="min-h-screen">
      <div className="pt-28 pb-24 md:pt-36 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Join Our Mission
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2C2A] font-medium mb-4">
              Become a Volunteer
            </h1>
            <p className="font-body text-base sm:text-lg text-[#6C6B66] max-w-xl">
              We need team members to help with the Pet Pantry, organize fundraisers,
              provide grooming services, set up doggy activities, and support shelters.
              Your time and skills make all the difference.
            </p>
          </motion.div>

          {/* Roles */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
          >
            {[
              { icon: Users, label: "Pantry Distribution", desc: "Help sort and distribute pet supplies" },
              { icon: Wrench, label: "Grooming Volunteer", desc: "Provide grooming services for pets" },
              { icon: Heart, label: "Fundraiser Organizer", desc: "Plan events and fundraising activities" },
              { icon: Clock, label: "Event Helper", desc: "Help run doggy activities and entertainment" },
            ].map((role, i) => (
              <div
                key={role.label}
                className="bg-[#F2EFEB] border border-[#E8E3DD] rounded-2xl p-5 flex gap-4 items-start"
                data-testid={`volunteer-role-${i}`}
              >
                <role.icon className="w-5 h-5 text-[#C0603A] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-body text-sm font-semibold text-[#2C2C2A]">{role.label}</p>
                  <p className="font-body text-xs text-[#6C6B66]">{role.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-5"
            data-testid="volunteer-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="volunteer-name-input"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="volunteer-email-input"
                />
              </div>
            </div>

            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
              <input
                type="tel"
                placeholder="Phone Number"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                data-testid="volunteer-phone-input"
              />
            </div>

            <select
              value={form.skills}
              onChange={(e) => update("skills", e.target.value)}
              className="w-full bg-white border border-[#E8E3DD] rounded-xl px-4 py-3.5 font-body text-sm text-[#3A3A38] focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow appearance-none"
              data-testid="volunteer-skills-select"
            >
              <option value="">What can you help with?</option>
              <option value="pantry">Pet Pantry Distribution</option>
              <option value="grooming">Grooming Services</option>
              <option value="fundraising">Fundraiser Planning</option>
              <option value="events">Event Organization</option>
              <option value="social_media">Social Media / Marketing</option>
              <option value="shelter">Shelter Support</option>
              <option value="other">Other</option>
            </select>

            <select
              value={form.availability}
              onChange={(e) => update("availability", e.target.value)}
              className="w-full bg-white border border-[#E8E3DD] rounded-xl px-4 py-3.5 font-body text-sm text-[#3A3A38] focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow appearance-none"
              data-testid="volunteer-availability-select"
            >
              <option value="">Your Availability</option>
              <option value="weekdays">Weekdays</option>
              <option value="weekends">Weekends</option>
              <option value="evenings">Evenings</option>
              <option value="flexible">Flexible</option>
            </select>

            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-[#6C6B66]" />
              <textarea
                placeholder="Tell us about yourself and why you want to volunteer"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow resize-none"
                data-testid="volunteer-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="volunteer-submit-button"
              className="bg-[#C0603A] text-white hover:bg-[#A64D29] disabled:opacity-60 transition-all duration-300 rounded-full px-8 py-4 font-body font-semibold text-base inline-flex items-center gap-2 hover:shadow-[0_12px_48px_rgba(192,96,58,0.2)]"
            >
              <Send className="w-4 h-4" />
              {loading ? "Submitting..." : "Join the Team"}
            </button>
          </motion.form>
        </div>
      </div>
      <Footer />
    </main>
  );
}
